import type { MbcBrandContext } from "@nlc/shared-types";

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";
import { startAlphaStepTransition } from "@/features/request/alpha/layout/alphaStepTransition";
import { NOT_STARTED_QUALITY } from "@/features/request/mbc-review/lib/brief-score-summary";
import { useGetAlphaDesignTypes } from "@/hooks/useGetAlphaDesignTypes";

import type { MbcRequestType } from "./lib/request-draft-api";

import { AlphaStepFrame } from "./components/AlphaStepFrame";
import { BrandGate } from "./components/BrandGate";
import { BriefSidebar } from "./components/BriefSidebar";
import { MBCChatHeader } from "./components/MBCChatHeader";
import { MBCConversation } from "./components/MBCConversation";
import { useLeaveMbcFlow } from "./hooks/useLeaveMbcFlow";
import { mapBrandContext } from "./lib/map-brand-context";

type MBCChatPageProps = {
  /** Numeric design-type id coming from the request-create picker. */
  designType?: number;
  /** Project the request is being created for; scopes the brand list/drafts. */
  projectId?: number;
  /** Request type of the design type picked; the draft is created with it. */
  requestType?: MbcRequestType;
  /** Resume: brand of the draft being reopened (re-grounds the agent). */
  resumeBrandId?: number;
  /** Resume: the draft's resource id. */
  resumeResourceId?: string;
  /** Resume: the draft's thread id. Presence = resume mode. */
  resumeThreadId?: string;
};

export function MBCChatPage({
  designType,
  projectId,
  requestType,
  resumeBrandId,
  resumeResourceId,
  resumeThreadId,
}: MBCChatPageProps) {
  const navigate = useNavigate();
  const { data: alphaDesignTypes, isPending: isPendingAlpha } =
    useGetAlphaDesignTypes();
  const isAlpha = Boolean(
    designType &&
      alphaDesignTypes?.designTypes.some(({ id }) => id === designType),
  );
  const handleBack = useLeaveMbcFlow(isAlpha);

  const [brand, setBrand] = useState<{
    context: MbcBrandContext | null;
    resolved: boolean;
  }>({ context: null, resolved: false });

  // Captured on mount: the chat writes its own threadId into the URL (so a
  // refresh can recover the session), and that must not flip a live fresh
  // conversation into resume mode mid-flight.
  const [isResume] = useState(() =>
    Boolean(resumeThreadId && resumeResourceId),
  );
  const { data: resumeBrand, isFetching: isFetchingResumeBrand } =
    useGetBrandById(resumeBrandId ?? 0);

  const handleCreateNewBrand = useCallback(() => {
    void navigate({ to: "/brands/create" });
  }, [navigate]);

  const shell = (children: React.ReactNode) => (
    <div
      className={twMerge(
        "mx-auto flex h-full min-h-0 flex-col",
        isAlpha
          ? "w-full p-md lg:p-xl"
          : "max-w-[1600px] overflow-hidden bg-bg-tertiary",
      )}
    >
      {children}
    </div>
  );

  const continueToStylesPlaceholder = (
    <Button isDisabled variant="primary">
      Continue to styles
    </Button>
  );
  const loadingDraft = (
    <p className="p-3xl text-sm text-text-tertiary">Loading draft…</p>
  );

  if (designType && isPendingAlpha && (isResume || brand.resolved)) {
    return shell(
      <>
        <MBCChatHeader designType={designType} onBack={handleBack} />
        <p className="p-3xl text-sm text-text-tertiary">Loading…</p>
      </>,
    );
  }

  if (isResume) {
    if (resumeBrandId && isFetchingResumeBrand) {
      return shell(
        isAlpha ? (
          <AlphaStepFrame
            onBack={handleBack}
            primaryAction={continueToStylesPlaceholder}
            step="Prompt"
          >
            {loadingDraft}
          </AlphaStepFrame>
        ) : (
          <>
            <MBCChatHeader
              brandId={resumeBrandId}
              designType={designType}
              onBack={handleBack}
            />
            {loadingDraft}
          </>
        ),
      );
    }

    return shell(
      <MBCConversation
        brand={resumeBrand ? mapBrandContext(resumeBrand) : null}
        designType={designType}
        isAlpha={isAlpha}
        projectId={projectId}
        requestType={requestType}
        resume={{ resourceId: resumeResourceId!, threadId: resumeThreadId! }}
      />,
    );
  }

  if (brand.resolved) {
    return shell(
      <MBCConversation
        brand={brand.context}
        designType={designType}
        isAlpha={isAlpha}
        projectId={projectId}
        requestType={requestType}
      />,
    );
  }

  const brandGate = (
    <div className="flex min-h-0 flex-1 gap-3xl p-3xl">
      <BrandGate
        onCreateNewBrand={handleCreateNewBrand}
        onResolved={(context) =>
          startAlphaStepTransition(() => setBrand({ context, resolved: true }))
        }
        projectId={projectId}
      />
      {isAlpha ? null : (
        <BriefSidebar
          canAddFiles={false}
          isCalculating={false}
          memory={null}
          onAddAssets={() => {}}
          onFilesChanged={() => {}}
          quality={NOT_STARTED_QUALITY}
        />
      )}
    </div>
  );

  return shell(
    isAlpha ? (
      <AlphaStepFrame
        onBack={handleBack}
        primaryAction={continueToStylesPlaceholder}
        step="Prompt"
      >
        {brandGate}
      </AlphaStepFrame>
    ) : (
      <>
        <MBCChatHeader designType={designType} onBack={handleBack} />
        {brandGate}
      </>
    ),
  );
}
